import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import React, { useCallback, useMemo } from "react";
import { LightTooltip } from "../../LightTooltip";
import { useFiltersContext } from "../../../pages/Workspace/UserFilters/FiltersContext";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import { useInsideFilterChip } from "../../../pages/Workspace/UserFilters/FiltersChips/hooks/useInsideFilterChip";
import {
  OpportunityTypeConfig,
  OpportunityTypeOption,
  getOpportunityTypeFromFilter,
  getOpportunityTypeLabel,
  getOpportunityTypeOptionTooltipType,
  isOpportunityTypeOptionDisabled,
  mapOpportunityTypeToFilter,
} from "./utils";
import { OpportunityTypeFilter as OpportunityTypeFilterType } from "../../../legacy/types/companyFilter";
import { Link as ReactDOMLink } from "react-router-dom";
import { FilterLayout } from "../UserFilters/FilterLayout";

interface OpportunityTypeFilterProps {
  value?: OpportunityTypeFilterType;
  onChange?: (value: OpportunityTypeFilterType | undefined) => void;
  config?: OpportunityTypeConfig;
}

export const OpportunityTypeFilter: React.FC<OpportunityTypeFilterProps> = ({
  value: controlledValue,
  onChange: controlledOnChange,
  config: controlledConfig,
}) => {
  const { draftFilters, setDraftFilters } = useFiltersContext();
  const { insideChip, elementRef } = useInsideFilterChip();
  const workspaceId = useWorkspaceStore((state) => state.draft?.id);
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  // Use controlled config if provided, otherwise create from workspace
  const config: OpportunityTypeConfig = useMemo(() => {
    if (controlledConfig) {
      return controlledConfig;
    }
    return {
      hasWhitespaceFile: Boolean(
        workspaceWhitespace?.fileId && workspaceWhitespace?.lastMatched
      ),
      enableHierarchyClassification: Boolean(
        workspaceWhitespace?.enableHierarchyClassification ?? false
      ),
      fileId: workspaceWhitespace?.fileId,
    };
  }, [controlledConfig, workspaceWhitespace]);

  // Use controlled value if in controlled mode, otherwise use draftFilters
  const currentValue = controlledOnChange
    ? controlledValue
    : draftFilters?.opportunityType;

  // Get current selected option
  const selectedOption = useMemo(
    () => getOpportunityTypeFromFilter(currentValue, config),
    [currentValue, config]
  );

  const handleOpportunityTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedOption = event.target.value as OpportunityTypeOption;

      // If "All Companies" is selected, clear the filter (set to undefined)
      const filterValue =
        selectedOption === OpportunityTypeOption.ALL_COMPANIES
          ? undefined
          : mapOpportunityTypeToFilter(selectedOption, config);

      if (controlledOnChange) {
        controlledOnChange(filterValue);
      } else {
        setDraftFilters((prev) => ({
          ...prev,
          opportunityType: filterValue,
        }));
      }
    },
    [config, controlledOnChange, setDraftFilters]
  );

  const handleClearFilter = useCallback(() => {
    if (controlledOnChange) {
      controlledOnChange(undefined);
    } else {
      setDraftFilters((prev) => ({
        ...prev,
        opportunityType: undefined,
      }));
    }
  }, [controlledOnChange, setDraftFilters]);

  const getTooltipContent = (tooltipType: string) => {
    const configLink = (
      <Link
        underline="hover"
        component={ReactDOMLink}
        color="primary"
        sx={{ fontStyle: "italic" }}
        to={`/projects/${workspaceId}/configuration?tab=whitespace`}
      >
        market configuration
      </Link>
    );

    switch (tooltipType) {
      case "customer":
        return (
          <Typography variant="body2">
            Upload a list of your customers within the whitespace tab of your{" "}
            {configLink} to filter your market to view only your existing
            customers.
          </Typography>
        );
      case "whitespace":
        return (
          <Typography variant="body2">
            Upload a list of your customers within the whitespace tab of your{" "}
            {configLink} to filter your market to view only companies that
            aren't your customers.
          </Typography>
        );
      case "expansion":
        return (
          <Typography variant="body2">
            Upload a list of your customers within the whitespace tab of your{" "}
            {configLink} to filter your market to view expansion opportunities
            within existing customer corporate hierarchies.
          </Typography>
        );
      case "prospect":
        return (
          <Typography variant="body2">
            Upload a list of your customers within the whitespace tab of your{" "}
            {configLink} to filter your market to view only net new prospects
            with no connection to your current customers.
          </Typography>
        );
      case "whitespace-disabled":
        return (
          <Typography variant="body2">
            Use Expansion Whitespace or Prospect Whitespace for more specific
            targeting.
          </Typography>
        );
      default:
        return null;
    }
  };

  const renderOptionWithDescription = (
    option: OpportunityTypeOption,
    description: string,
    isIndented: boolean = false
  ) => {
    const isOptionDisabled = isOpportunityTypeOptionDisabled(option, config);
    const tooltipType = getOpportunityTypeOptionTooltipType(option, config);
    const tooltipContent = tooltipType ? getTooltipContent(tooltipType) : null;

    // Always allow "All Companies" to be selected when editing (to allow clearing the filter)
    const shouldDisableOption =
      isOptionDisabled && option !== OpportunityTypeOption.ALL_COMPANIES;

    return (
      <Box key={option} sx={{ ml: isIndented ? 3 : 0, mb: 2 }}>
        <FormControlLabel
          value={option}
          control={<Radio />}
          disabled={shouldDisableOption}
          sx={{
            width: "100%",
            margin: 0,
            alignItems: "flex-start",
          }}
          label={
            <Box sx={{ ml: 1, width: "100%" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body1"
                  color={shouldDisableOption ? "text.disabled" : "text.primary"}
                  sx={{ fontWeight: 500 }}
                >
                  {getOpportunityTypeLabel(option)}
                </Typography>
                {shouldDisableOption && tooltipContent && (
                  <LightTooltip title={tooltipContent} placement="right">
                    <IconButton
                      size="small"
                      sx={{ p: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <WarningAmberIcon
                        sx={{
                          fontSize: 16,
                          color: "#ff9800",
                        }}
                      />
                    </IconButton>
                  </LightTooltip>
                )}
              </Stack>
              <Typography
                variant="body2"
                color={shouldDisableOption ? "text.disabled" : "text.secondary"}
                sx={{ mt: 0.5 }}
              >
                {description}
              </Typography>
            </Box>
          }
        />
      </Box>
    );
  };

  return (
    <FilterLayout
      title="Opportunity Type"
      onClear={handleClearFilter}
      showHeader={!insideChip}
      id="opportunity-type-filter"
      refProp={elementRef}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Categorizes companies based on their relationship to your uploaded
          customer list. When basic whitespace is configured, you can filter
          between customers and whitespace opportunities. When hierarchy
          analysis is enabled, you can further distinguish between expansion
          opportunities within existing customer corporate structures and
          completely net new prospects. Change this setting{" "}
          <Link
            component={ReactDOMLink}
            to={`/projects/${workspaceId}/configuration?tab=whitespace`}
            underline="hover"
            color="primary"
          >
            here
          </Link>
          .
        </Typography>
      </Box>

      <RadioGroup value={selectedOption} onChange={handleOpportunityTypeChange}>
        {renderOptionWithDescription(
          OpportunityTypeOption.ALL_COMPANIES,
          "All companies in your market"
        )}

        {renderOptionWithDescription(
          OpportunityTypeOption.CUSTOMER,
          "Companies that directly match your uploaded customer list, defined in the market configuration"
        )}

        {renderOptionWithDescription(
          OpportunityTypeOption.WHITESPACE,
          "Companies in your market that are not current customers"
        )}

        {renderOptionWithDescription(
          OpportunityTypeOption.EXPANSION_WHITESPACE,
          "Companies related to your existing customers through corporate hierarchies but not direct matches",
          true
        )}

        {renderOptionWithDescription(
          OpportunityTypeOption.PROSPECT_WHITESPACE,
          "Companies with no relationship to your current customer base",
          true
        )}
      </RadioGroup>
    </FilterLayout>
  );
};
